import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, panelGet } from '../../kit';
import { DateRange, Labeled, MultiSelect, inputCls } from '../emailKit';
import { TagFilter, TagFilterValue, tagCell } from '../../tags/shared';
import {
  EXCLUDED_LABEL, Extra, LastEmailed, Person, Team, downloadPeopleCsv, labelize, num, parseEmails,
} from './shared';

/**
 * Step 1 — who gets the e-mail.
 *
 *  · A group: the shared tag filter (lifecycle, persona, cohort tags incl. the
 *    automatic ones, the date facts) plus this tab's own filters. Every
 *    matching person is listed and ticked; unticking leaves them out (they
 *    travel as exclude_user_ids and are named in the campaign record).
 *  · Specific people: search existing accounts (prospects included) and pick.
 *    Nobody can be typed in who is not already a user.
 * Each person shows when we last e-mailed them — a note, never a filter.
 */

export type AudienceMode = 'group' | 'people';
/** `sig`: the request the loaded `data` answers (the list for other filters is stale). */
export type ListState = { data: any; loading: boolean; error: string; sig?: string };

const ModeToggle: React.FC<{ mode: AudienceMode; onMode: (m: AudienceMode) => void }> = ({ mode, onMode }) => (
  <div className="flex gap-1">
    {([['group', 'A group (filters)'], ['people', 'Specific people']] as const).map(([k, label]) => (
      <button key={k} type="button" onClick={() => onMode(k)}
        className={`py-1.5 px-3 rounded-full text-xs font-grotesk ${mode === k
          ? 'bg-primary-500 text-grayscale-100 font-bold'
          : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
        {label}
      </button>
    ))}
  </div>
);

const PEOPLE_COLUMNS = ['email', 'name', 'org', 'lifecycle', 'last_emailed_at', 'country'];
const PEOPLE_LABELS = { org: 'Organisation', lifecycle: 'Lifecycle · tags', last_emailed_at: 'Last e-mailed' };
const peopleRender = {
  lifecycle: tagCell,
  last_emailed_at: (r: Record<string, any>) => <LastEmailed at={r.last_emailed_at} />,
};

export const AudienceStep: React.FC<{
  mode: AudienceMode; onMode: (m: AudienceMode) => void;
  tf: TagFilterValue; onTf: (v: TagFilterValue) => void;
  extra: Extra; onExtra: (patch: Partial<Extra>) => void;
  templates: { key: string; name: string }[];
  countries: { country: string; n: number }[];
  totals?: { users?: number; team?: number; bounced?: number };
  list: ListState; tooLong: boolean;
  unticked: Set<string>; onUnticked: (s: Set<string>) => void;
  people: Person[]; onPeople: (p: Person[]) => void;
  cap: number;
  onClear: () => void;
}> = ({ mode, onMode, tf, onTf, extra, onExtra, templates, countries, totals, list, tooLong,
        unticked, onUnticked, people, onPeople, cap, onClear }) => {
  const tplLabels = Object.fromEntries(templates.map(t => [t.key, t.name]));
  const countryLabels = Object.fromEntries(countries.map(c => [c.country, `${c.country} (${num(c.n)})`]));
  return (
    <Section title="1 · Audience"
      right={<div className="flex items-center gap-2 flex-wrap">
        {totals?.users !== undefined && (
          <span className="text-xs text-grayscale-1100">
            {num(totals.users)} users · {num(totals.team)} team · {num(totals.bounced)} bounced
          </span>
        )}
        <ModeToggle mode={mode} onMode={onMode} />
        <Btn small onClick={onClear}>Clear</Btn>
      </div>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Inactive accounts, open deletion requests, and addresses that bounced, were blocked or reported spam are
        always left out (resolve a bounce in Deliverability to allow it again).
      </p>
      {mode === 'group' ? (
        <>
          <TagFilter value={tf} onChange={v => onTf(v)} />
          <div className="flex flex-wrap items-end gap-3">
            <MultiSelect label="Country" options={countries.map(c => c.country)} labels={countryLabels}
              values={extra.countries} onChange={v => onExtra({ countries: v })} placeholder="Any country" allowFree />
            <Labeled label="Organisation contains">
              <input value={extra.organisation} onChange={e => onExtra({ organisation: e.target.value })}
                placeholder="e.g. Tata" className={`${inputCls} !w-40`} />
            </Labeled>
            <Labeled label="Designation contains">
              <input value={extra.designation} onChange={e => onExtra({ designation: e.target.value })}
                placeholder="e.g. R&D" className={`${inputCls} !w-40`} />
            </Labeled>
            <Labeled label="Name or e-mail contains">
              <input value={extra.q} onChange={e => onExtra({ q: e.target.value })}
                placeholder="e.g. @tata.com" className={`${inputCls} !w-40`} />
            </Labeled>
            <Labeled label="Searches (min – max)">
              <div className="flex items-center gap-1">
                <input type="number" min={0} value={extra.min_searches} placeholder="min"
                  onChange={e => onExtra({ min_searches: e.target.value })} className={`${inputCls} !w-20`} />
                <span>–</span>
                <input type="number" min={0} value={extra.max_searches} placeholder="max"
                  onChange={e => onExtra({ max_searches: e.target.value })} className={`${inputCls} !w-20`} />
              </div>
            </Labeled>
            <Labeled label="Our team (@universitytech.io + billing admins)">
              <Select value={extra.team} onChange={v => onExtra({ team: v as Team })}
                options={['exclude', 'include', 'only']}
                labels={{ exclude: 'Leave the team out', include: 'Include the team', only: 'Team only (a test run)' }} />
            </Labeled>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <DateRange label="Trial started between" from={extra.trial_started_from} to={extra.trial_started_to}
              onChange={(a, b) => onExtra({ trial_started_from: a, trial_started_to: b })} />
            <MultiSelect label="Has received any of" options={templates.map(t => t.key)} labels={tplLabels}
              values={extra.received_template} onChange={v => onExtra({ received_template: v })} placeholder="No condition" />
            <MultiSelect label="Has received none of" options={templates.map(t => t.key)} labels={tplLabels}
              values={extra.not_received_template} onChange={v => onExtra({ not_received_template: v })} placeholder="No condition" />
          </div>
          <Labeled label="Only these addresses (paste — optional; the other filters still apply)"
            hint={extra.emails.trim() ? `${parseEmails(extra.emails).length} address(es) recognised` : undefined}>
            <textarea value={extra.emails} rows={2} onChange={e => onExtra({ emails: e.target.value })}
              placeholder="one@company.com, two@company.com — or one per line"
              className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 font-mono" />
          </Labeled>
          {tooLong ? (
            <Msg kind="error">
              These filters are too long for one request (usually a long pasted list). Paste fewer addresses — a
              few hundred at a time — or pick the people under Specific people.
            </Msg>
          ) : (
            <GroupList list={list} unticked={unticked} onUnticked={onUnticked} cap={cap} />
          )}
        </>
      ) : (
        <PeoplePicker people={people} onPeople={onPeople} cap={cap} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// The ticked list of a group
// ---------------------------------------------------------------------------
const GroupList: React.FC<{
  list: ListState; unticked: Set<string>; onUnticked: (s: Set<string>) => void; cap: number;
}> = ({ list, unticked, onUnticked, cap }) => {
  const d: any = list.data || {};
  const rows: Person[] = d.rows || [];
  const excludedRows: Person[] = d.excluded_rows || [];
  const view = useMemo(() => rows.map(r => ({ ...r, tick: !unticked.has(r.user_id) })), [rows, unticked]);
  const ticked = view.filter(r => r.tick).length;
  const toggle = (id: string) => {
    const next = new Set(unticked);
    if (next.has(id)) next.delete(id); else next.add(id);
    onUnticked(next);
  };
  const excluded: Record<string, number> = d.excluded || {};
  if (list.error) return <Msg kind="error">{list.error}</Msg>;
  if (!list.data) return <Loading label="Finding the people these filters match…" />;
  return (
    <div className="flex flex-col gap-2 border-t border-grayscale-200 pt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">
          {num(ticked)} ticked of {num(d.count)} matching
        </span>
        {list.loading && <span className="text-xs text-grayscale-1100">updating…</span>}
        {Number(d.excluded_total || 0) > 0 && (
          <span className="text-xs text-grayscale-1100">
            · {num(d.excluded_total)} left out automatically (
            {Object.entries(excluded).filter(([, v]) => Number(v) > 0)
              .map(([k, v]) => `${num(v)} ${(EXCLUDED_LABEL[k] || labelize(k)).toLowerCase()}`).join(', ')})
          </span>
        )}
        <span className="flex-1" />
        <Btn small onClick={() => onUnticked(new Set())} disabled={!unticked.size}>Tick all</Btn>
        <Btn small onClick={() => onUnticked(new Set(rows.map(r => r.user_id)))} disabled={!rows.length}>Untick all</Btn>
        <Btn small onClick={() => downloadPeopleCsv('campaign_audience', [...view.map(r => ({ ...r, ticked: r.tick })), ...excludedRows])}
          disabled={!rows.length && !excludedRows.length}>Export list (CSV)</Btn>
      </div>
      {d.over_cap && (
        <Msg kind="error">
          {num(d.count)} people match — one campaign is capped at {num(d.cap || cap)}. Narrow the filters (a date
          range works well) and send in more than one campaign. The list shows the first {num(d.cap || cap)}.
        </Msg>
      )}
      {(d.unmatched_emails || []).length > 0 && (
        <div className="text-xs text-amber-800">
          {d.unmatched_emails.length} pasted address(es) are not users:{' '}
          {d.unmatched_emails.slice(0, 20).join(', ')}{d.unmatched_emails.length > 20 ? ' …' : ''}
        </div>
      )}
      <DataTable rows={view} maxHeight="26rem" defaultSort="email"
        columns={['tick', ...PEOPLE_COLUMNS]}
        labels={{ tick: '✓', ...PEOPLE_LABELS }}
        render={{
          tick: r => (
            <input type="checkbox" className="cursor-pointer accent-primary-500" checked={!!r.tick}
              onChange={() => toggle(String(r.user_id))} aria-label={`Include ${r.email}`}
              title={r.tick ? 'Untick to leave this person out' : 'Tick to include this person'} />
          ),
          ...peopleRender,
        }}
        empty="Nobody matches these filters." />
      {excludedRows.length > 0 && (
        <details>
          <summary className="text-xs text-grayscale-1100 cursor-pointer">
            Left out automatically, and why ({num(d.excluded_total)})
          </summary>
          <div className="mt-2">
            <DataTable rows={excludedRows} maxHeight="18rem"
              columns={['email', 'name', 'reason', 'detail', 'lifecycle', 'last_emailed_at']}
              labels={PEOPLE_LABELS}
              render={{ reason: r => <span>{EXCLUDED_LABEL[r.reason] || r.reason}</span>, ...peopleRender }} />
          </div>
        </details>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Picking people one by one
// ---------------------------------------------------------------------------
const PeoplePicker: React.FC<{ people: Person[]; onPeople: (p: Person[]) => void; cap: number }> =
  ({ people, onPeople, cap }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Person[]>([]);
  const [state, setState] = useState<{ loading: boolean; error: string }>({ loading: false, error: '' });
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); setState({ loading: false, error: '' }); return; }
    const t = setTimeout(async () => {
      const mine = ++seq.current;
      setState({ loading: true, error: '' });
      const { data, error } = await panelGet('email', 'target_people_search', { q: term });
      if (mine !== seq.current) return;
      setHits(data?.rows || []);
      setState({ loading: false, error });
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const picked = new Set(people.map(p => p.user_id));
  const add = (p: Person) => { if (!picked.has(p.user_id) && people.length < cap) onPeople([...people, p]); };
  const remove = (id: string) => onPeople(people.filter(p => p.user_id !== id));
  return (
    <div className="flex flex-col gap-3">
      <Labeled label="Find people by name, e-mail or organisation (existing accounts, prospects included)">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Type at least 2 characters…"
          className={`${inputCls} max-w-md`} autoFocus />
      </Labeled>
      {state.error && <Msg kind="error">{state.error}</Msg>}
      {q.trim().length >= 2 && (
        state.loading && !hits.length ? <Loading label="Searching…" /> : (
          <DataTable rows={hits} maxHeight="16rem" columns={PEOPLE_COLUMNS} labels={PEOPLE_LABELS}
            render={peopleRender}
            actions={r => picked.has(r.user_id)
              ? <span className="text-xs text-grayscale-1100">added</span>
              : <Btn small onClick={() => add(r as Person)} disabled={people.length >= cap}>Add</Btn>}
            empty="No account matches." />
        )
      )}
      <div className="flex items-center gap-2 flex-wrap border-t border-grayscale-200 pt-3">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">
          {num(people.length)} {people.length === 1 ? 'person' : 'people'} picked
        </span>
        <span className="flex-1" />
        <Btn small onClick={() => onPeople([])} disabled={!people.length}>Remove all</Btn>
        <Btn small onClick={() => downloadPeopleCsv('campaign_people', people)} disabled={!people.length}>
          Export list (CSV)
        </Btn>
      </div>
      <DataTable rows={people} maxHeight="22rem" columns={PEOPLE_COLUMNS} labels={PEOPLE_LABELS}
        render={peopleRender}
        actions={r => <Btn small onClick={() => remove(String(r.user_id))}>Remove</Btn>}
        empty="Nobody picked yet — search above, or send people here from the People directory." />
    </div>
  );
};
